import React, { useState, useEffect } from "react";
import { Badge, Icon, Text, Heading, Button, TextField } from "@telia/teddy";
import cn from "classnames";
import _get from "lodash/get";
import _includes from "lodash/includes";
import qr from "qrcode";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { EsimProduct } from "../../../../../../models/state";
import { Alert } from "../../../../components";
import { sendEmailWithQRCode } from "../../../state/actions";
import TEXT from "../../../text-consts";
import "./EsimCard.less";

const simTypes = (type: string) => {
    const upperCaseType = type.toUpperCase();
    if (_includes(upperCaseType, "MAIN") || _includes(upperCaseType, "HOVED")) {
        return TEXT.SIM_TYPE.MAIN_SIM;
    } else if (_includes(upperCaseType, "DATA")) {
        return TEXT.SIM_TYPE.EXTRA_DATA_SIM;
    } else if (
        _includes(upperCaseType, "TVILLING") ||
        _includes(upperCaseType, "TWIN") ||
        _includes(upperCaseType, "TRILLING")
    ) {
        return TEXT.SIM_TYPE.TWIN_SIM;
    } else if (_includes(upperCaseType, "REPLACE") || _includes(upperCaseType, "ERSTATNING")) {
        return TEXT.SIM_TYPE.REPLACEMENT_SIM;
    } else {
        return TEXT.SIM_TYPE.UNKNOWN;
    }
};

type BadgeVariant = "attention" | "error" | "information" | "neutral" | "special" | "success" | "warning";

const getBadgeVariant = (status: string): BadgeVariant => {
    switch (status) {
        case "positive":
            return "success";
        case "negative":
            return "error";
        case "warning":
            return "warning";
        case "neutral":
            return "neutral";
        case "communication":
        case "info":
        default:
            return "information";
    }
};

type EsimCardProps = Omit<EsimProduct, "subscriptionPhoneNumber">;

const EsimCard: React.FC<EsimCardProps> = ({
    productUser,
    phoneNumber,
    type,
    profileStatus,
    iccid,
    activationCode,
}) => {
    const { orderGuid } = useParams() as { orderGuid: string };
    const dispatch = useDispatch();

    const [isExpanded, setIsExpanded] = useState(false);
    const [QRCode, setQRCode] = useState<string | null>();
    const [email, setEmail] = useState("");
    const [isEmailSent, setIsEmailSent] = useState(false);
    const [emailAddressSentTo, setEmailAddressSentTo] = useState("");

    useEffect(() => {
        qr.toDataURL(activationCode, (err, url) => {
            if (err) {
                setQRCode(null);
            } else {
                setQRCode(url);
            }
        });
    }, [activationCode]);

    const sendEmail = async () => {
        await dispatch(sendEmailWithQRCode(orderGuid, email, iccid));
        setIsEmailSent(true);
        setEmailAddressSentTo(email);
        setEmail("");
    };

    const renderSendEmailInput = () => (
        <>
            <Heading mb="100" as="h3" variant="title-200">
                {TEXT.ESIM.ESIM_SEND_QR}
            </Heading>
            {isEmailSent && <Alert kind="positive">{TEXT.ESIM.ESIM_SEND_CONFIRMATION(emailAddressSentTo)}</Alert>}
            <TextField className="esim-card__send-link">
                <TextField.Label>På e-post:</TextField.Label>
                <TextField.Input value={email} onChange={(e) => setEmail(e.target.value)} data-di-mask />
            </TextField>
            <Button variant="primary" id="install-esim-send-link-button" type="submit" size="sm" onClick={sendEmail}>
                <Icon name="send" /> {TEXT.ESIM.ESIM_SEND_QR_BUTTON}
            </Button>
        </>
    );

    const isAllowedToInstall = _get(profileStatus, "isAllowedToInstall", false);
    const isInstalled = _get(profileStatus, "isInstalled", false);
    const isExpandable = isAllowedToInstall;

    return (
        <div className={cn("esim-card", { "esim-card--expandable": isExpandable })}>
            <div
                className={cn("esim-card__user", { "esim-card__user--expandable": isExpandable })}
                onClick={() => isExpandable && setIsExpanded(!isExpanded)}
            >
                {isExpandable && (
                    <div className="esim-card__toggle-card-icon">
                        {isExpanded ? <Icon name="chevron-up" /> : <Icon name="chevron-down" />}
                    </div>
                )}

                <div className="esim-card__name">
                    <Heading mb="100" as="h3" variant="title-200">
                        {simTypes(type)}
                    </Heading>
                    <Badge variant={getBadgeVariant(profileStatus.status)} data-di-mask>
                        {profileStatus.label}
                    </Badge>
                </div>
                <div className="esim-card__product-user" data-di-mask>
                    <div>
                        <span className="esim-card__product-user--info">
                            {`${productUser?.firstName || ""} ${productUser?.lastName || ""}`.trim()}
                        </span>
                        <span id="phoneNumber" className="esim-card__product-user--info">
                            {phoneNumber}
                        </span>
                    </div>
                </div>
                <span id="esim-nr" className="esim-card__product-user--info">
                    eSIM-nr: {iccid}
                </span>
                {profileStatus.description && (
                    <Text mb="100" className="esim-card__install-description" variant="additional-100">
                        {profileStatus.description}
                    </Text>
                )}
            </div>
            {isExpanded && (
                <div className="esim-card__content">
                    {isAllowedToInstall && !isInstalled && (
                        <div className="esim-card__install-device">
                            <div>
                                <Heading mb="100" as="h3" variant="title-200">
                                    {TEXT.ESIM.ESIM_SCAN_QR_HEADING}
                                </Heading>
                                {QRCode && (
                                    <div className="esim-card__qr-code-container" data-di-mask>
                                        <Text mb="100">{TEXT.ESIM.ESIM_SCAN_QR_PARAGRAPH}</Text>
                                        <img src={QRCode} id="qr-code" alt="qr-code" />
                                    </div>
                                )}
                                {renderSendEmailInput()}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EsimCard;
